import React, { useState, useEffect } from 'react'
import {
  Avatar,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useIntl } from 'react-intl'
import { colorPalette } from '../../../../style/colorPalette'
import { SpiAdjustmentItem, SprintSelectionItem } from './types'
import { TableHeaderCell, TableDataCell } from './StyledComponents'
import { NumberTextInput } from '../../../../components/inputs/NumberTextInput'
import { UserBasic } from '../../../../../lib/functions/user'

interface Props {
  spiItems: SpiAdjustmentItem[]
  selectionItems: SprintSelectionItem[]
  workloadUnitLabel: string
  addMenuAnchor: HTMLElement | null
  teamMembers: UserBasic[]
  onAddMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void
  onAddMenuClose: () => void
  onAddSpi: (sprintUuid: string) => void
  onRemoveSpi: (spiUuid: string) => void
  onWorkloadChange: (spiUuid: string, value: string) => void
  onAssigneeChange: (spiUuid: string, assignee: UserBasic | null) => void
  onSwitchToSelection: () => void
  formatDate: (dateStr: string) => string
  convertWorkloadFromHourToSelectedUnit: (hour: number) => number
}

export const WorkloadModeView: React.FC<Props> = ({
  spiItems,
  selectionItems,
  workloadUnitLabel,
  addMenuAnchor,
  teamMembers,
  onAddMenuOpen,
  onAddMenuClose,
  onAddSpi,
  onRemoveSpi,
  onWorkloadChange,
  onAssigneeChange,
  onSwitchToSelection,
  formatDate,
  convertWorkloadFromHourToSelectedUnit,
}) => {
  const intl = useIntl()

  // 工数入力のローカル表示値（空文字入力を許容するため）
  const [workloadInputValues, setWorkloadInputValues] = useState<
    Record<string, string>
  >({})

  return (
    <>
      {/* アクションバー */}
      <Box
        sx={{
          px: 3,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={onAddMenuOpen}
          disabled={selectionItems.length === 0}
          size="small"
        >
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.addSprint',
          })}
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={onAddMenuClose}
        >
          {selectionItems.map(item => (
            <MenuItem
              key={item.sprintUuid}
              onClick={() => onAddSpi(item.sprintUuid)}
            >
              {item.sprintName}（{formatDate(item.startDate)}～
              {formatDate(item.endDate)}）
            </MenuItem>
          ))}
        </Menu>
        <Button size="small" onClick={onSwitchToSelection}>
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.editSelection',
          })}
        </Button>
      </Box>
      {/* SPI一覧テーブル */}
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.sprint',
                })}
              </TableHeaderCell>
              <TableHeaderCell sx={{ width: 160 }}>
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.pbiAssignee',
                })}
              </TableHeaderCell>
              <TableHeaderCell align="right">
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.estimatedHour',
                })}
                ({workloadUnitLabel})
              </TableHeaderCell>
              <TableHeaderCell align="center" sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {spiItems.length === 0 ? (
              <TableRow>
                <TableDataCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {intl.formatMessage({
                      id: 'productBacklog.sprintInteractiveAdjustment.noSprints',
                    })}
                  </Typography>
                </TableDataCell>
              </TableRow>
            ) : (
              spiItems.map(item => (
                <TableRow key={item.uuid} hover={true}>
                  <TableDataCell>
                    {item.sprintName}（{formatDate(item.startDate)}～
                    {formatDate(item.endDate)}）
                  </TableDataCell>
                  <TableDataCell
                    sx={{
                      verticalAlign: 'middle',
                      padding: 0,
                    }}
                  >
                    <AssigneeAutocomplete
                      value={item.assignee}
                      options={teamMembers}
                      onChange={assignee =>
                        onAssigneeChange(item.uuid, assignee)
                      }
                      placeholder={intl.formatMessage({
                        id: 'productBacklog.sprintInteractiveAdjustment.searchAssignee',
                      })}
                    />
                  </TableDataCell>
                  <TableDataCell align="right">
                    <NumberTextInput
                      value={
                        workloadInputValues[item.uuid] ??
                        convertWorkloadFromHourToSelectedUnit(
                          item.estimatedHour
                        )
                      }
                      onChange={e => {
                        const value = e.target.value
                        setWorkloadInputValues(prev => ({
                          ...prev,
                          [item.uuid]: value,
                        }))
                        onWorkloadChange(item.uuid, value)
                      }}
                      inputProps={{
                        min: 0,
                        step: 1,
                        onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                          e.target.select(),
                        onBlur: () => {
                          // フォーカスが外れたらローカル値をクリア（親データに同期）
                          setWorkloadInputValues(prev => {
                            const newValues = { ...prev }
                            delete newValues[item.uuid]
                            return newValues
                          })
                        },
                      }}
                      sx={{
                        width: 100,
                        '& > input': {
                          textAlign: 'right',
                          padding: '6px 8px',
                          border: `1px solid ${colorPalette.monotone[2]}`,
                          borderRadius: '4px',
                          '&:focus': {
                            borderColor: colorPalette.skyBlue[7],
                            outline: 'none',
                          },
                        },
                      }}
                    />
                  </TableDataCell>
                  <TableDataCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveSpi(item.uuid)}
                      sx={{ color: colorPalette.monotone[4] }}
                      tabIndex={-1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableDataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

// セル内Autocompleteコンポーネント（React.memoで再レンダリングを防止）
interface AssigneeAutocompleteProps {
  value: UserBasic | null | undefined
  options: UserBasic[]
  onChange: (assignee: UserBasic | null) => void
  placeholder: string
}

const AssigneeAutocomplete = React.memo<AssigneeAutocompleteProps>(
  ({ value, options, onChange, placeholder }) => {
    const [inputValue, setInputValue] = useState(value?.name ?? '')

    // valueが外部から変更された場合に同期
    useEffect(() => {
      setInputValue(value?.name ?? '')
    }, [value?.name])

    return (
      <Autocomplete
        value={value ?? null}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // 'reset'で空文字の場合のみ無視（親の再レンダリングによる不要なクリアを防止）
          if (reason !== 'reset' || newInputValue !== '') {
            setInputValue(newInputValue)
          }
        }}
        options={options}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, val) => option.uuid === val.uuid}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.uuid}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Avatar src={option.iconUrl} sx={{ width: 24, height: 24 }} />
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            inputProps={{
              ...params.inputProps,
              onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                e.target.select(),
            }}
          />
        )}
        onChange={(_, newValue) => onChange(newValue)}
        size="small"
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            padding: '2px 8px',
            '& fieldset': {
              borderColor: colorPalette.monotone[2],
            },
            '&:hover fieldset': {
              borderColor: colorPalette.skyBlue[7],
            },
            '&.Mui-focused fieldset': {
              borderColor: colorPalette.skyBlue[7],
              borderWidth: 1,
            },
          },
        }}
        openOnFocus={true}
        autoHighlight={true}
      />
    )
  },
  // onChangeは毎回新しい関数なので比較から除外
  (prevProps, nextProps) =>
    prevProps.value?.uuid === nextProps.value?.uuid &&
    prevProps.options === nextProps.options &&
    prevProps.placeholder === nextProps.placeholder
)
